import type { SubtitleModel } from '@project/common/src/model';
import hotkeys from 'hotkeys-js';
import { bindMouseShortcut, isMouseShortcut } from '@project/common/key-binder/mouse-shortcut';
import type { KeyBindSet, SeekableTracks, TokenJumpTarget } from '@project/common/settings';
import { isTrackSeekable, TokenState, TokenStatus } from '@project/common/settings';
import { adjacentSubtitle } from '@project/common/util';

export type ShortcutEvent = KeyboardEvent | MouseEvent;

export interface KeyBinder {
    bindMineNewJitenWords?: (onMine: (event: ShortcutEvent) => void, disabledGetter: () => boolean) => () => void;
    bindCopy<T extends SubtitleModel = SubtitleModel>(
        onCopy: (event: ShortcutEvent, subtitle: T) => void,
        disabledGetter: () => boolean,
        subtitleGetter: () => T | undefined,
        capture?: boolean
    ): () => void;
    bindAnkiExport(
        onAnkiExport: (event: ShortcutEvent) => void,
        disabledGetter: () => boolean,
        capture?: boolean
    ): () => void;
    bindUpdateLastCard(
        onUpdateLastCard: (event: ShortcutEvent) => void,
        disabledGetter: () => boolean,
        capture?: boolean
    ): () => void;
    bindTakeScreenshot(
        onTakeScreenshot: (event: ShortcutEvent) => void,
        disabledGetter: () => boolean,
        capture?: boolean
    ): () => void;
    bindSeekToSubtitle(
        onSeekToSubtitle: (event: ShortcutEvent, subtitle: SubtitleModel) => void,
        disabledGetter: () => boolean,
        timeGetter: () => number,
        subtitlesGetter: () => SubtitleModel[] | undefined,
        seekableTracksGetter: () => SeekableTracks,
        capture?: boolean
    ): () => void;
    bindSeekToBeginningOfCurrentSubtitle(
        onSeekToBeginningOfCurrentSubtitle: (event: ShortcutEvent, subtitle: SubtitleModel) => void,
        disabledGetter: () => boolean,
        timeGetter: () => number,
        subtitlesGetter: () => SubtitleModel[] | undefined,
        seekableTracksGetter: () => SeekableTracks,
        capture?: boolean
    ): () => void;
    bindSeekBackwardOrForward(
        onSeekBackwardOrForward: (event: ShortcutEvent, forward: boolean) => void,
        disabledGetter: () => boolean,
        capture?: boolean
    ): () => void;
    bindOffsetToSubtitle(
        onOffsetChange: (event: ShortcutEvent, newOffset: number) => void,
        disabledGetter: () => boolean,
        timeGetter: () => number,
        subtitlesGetter: () => SubtitleModel[] | undefined,
        seekableTracksGetter: () => SeekableTracks,
        capture?: boolean
    ): () => void;
    bindAdjustOffset(
        onOffsetChange: (event: ShortcutEvent, newOffset: number) => void,
        disabledGetter: () => boolean,
        subtitlesGetter: () => SubtitleModel[] | undefined,
        capture?: boolean
    ): () => void;
    bindResetOffet(
        onResetOffset: (event: ShortcutEvent) => void,
        disabledGetter: () => boolean,
        capture?: boolean
    ): () => void;
    bindAdjustPlaybackRate(
        onAdjustPlaybackRate: (event: ShortcutEvent, increase: boolean) => void,
        disabledGetter: () => boolean,
        capture?: boolean
    ): () => void;
    bindToggleSubtitles(
        onToggleSubtitles: (event: ShortcutEvent) => void,
        disabledGetter: () => boolean,
        capture?: boolean
    ): () => void;
    bindToggleSubtitleStudy(
        onToggleSubtitleStudy: (event: ShortcutEvent, mode: 'blur' | 'hide') => void,
        disabledGetter: () => boolean,
        capture?: boolean
    ): () => void;
    bindToggleSubtitleTrackInVideo(
        onToggleSubtitleTrack: (event: ShortcutEvent, extra: any) => void,
        disabledGetter: () => boolean,
        capture?: boolean
    ): () => void;
    bindToggleSubtitleTrackInList(
        onToggleSubtitleTrackInList: (event: ShortcutEvent, extra: any) => void,
        disabledGetter: () => boolean,
        capture?: boolean
    ): () => void;
    bindUnblurTrack(
        onUnblurTrack: (event: ShortcutEvent, track: number) => void,
        disabledGetter: () => boolean,
        capture?: boolean
    ): () => void;
    bindPlay(onPlay: (event: ShortcutEvent) => void, disabledGetter: () => boolean, capture?: boolean): () => void;
    bindAutoPause(
        onAutoPause: (event: ShortcutEvent) => void,
        disabledGetter: () => boolean,
        capture?: boolean
    ): () => void;
    bindCondensedPlayback(
        onCondensedPlayback: (event: ShortcutEvent) => void,
        disabledGetter: () => boolean,
        capture?: boolean
    ): () => void;
    bindFastForwardPlayback(
        onFastForwardPlayback: (event: ShortcutEvent) => void,
        disabledGetter: () => boolean,
        capture?: boolean
    ): () => void;
    bindToggleSidePanel(
        onToggleSidePanel: (event: ShortcutEvent) => void,
        disabledGetter: () => boolean,
        capture?: boolean
    ): () => void;
    bindToggleRepeat(
        onToggleRepeat: (event: ShortcutEvent) => void,
        disabledGetter: () => boolean,
        capture?: boolean
    ): () => void;
    bindCycleAutoPauseResumeMode(
        onCycleAutoPauseResumeMode: (event: ShortcutEvent) => void,
        disabledGetter: () => boolean,
        capture?: boolean
    ): () => void;
    bindToggleSubtitleVisibility(
        onToggleSubtitleVisibility: (event: ShortcutEvent) => void,
        disabledGetter: () => boolean,
        capture?: boolean
    ): () => void;
    bindToggleRecording(
        onToggleRecording: (event: ShortcutEvent) => void,
        disabledGetter: () => boolean,
        capture?: boolean
    ): () => void;
    bindSelectSubtitleTrack(
        onSelectSubtitleTrack: (event: ShortcutEvent) => void,
        disabledGetter: () => boolean,
        capture?: boolean
    ): () => void;
    bindAdjustSubtitlePositionOffset(
        onAdjustSubtitlePositionOffset: (event: ShortcutEvent, increase: boolean) => void,
        disabledGetter: () => boolean,
        capture?: boolean
    ): () => void;
    bindAdjustTopSubtitlePositionOffset(
        onAdjustTopSubtitlePositionOffset: (event: ShortcutEvent, increase: boolean) => void,
        disabledGetter: () => boolean,
        capture?: boolean
    ): () => void;
    bindOpenStatistics(
        onOpenStatistics: (event: ShortcutEvent) => void,
        disabledGetter: () => boolean,
        capture?: boolean
    ): () => void;
    bindMarkHoveredToken(
        onMarkHoveredToken: (event: ShortcutEvent, tokenStatus: TokenStatus) => void,
        disabledGetter: () => boolean,
        capture?: boolean
    ): () => void;
    bindToggleHoveredTokenIgnored(
        onToggleHoveredTokenIgnored: (event: ShortcutEvent) => void,
        disabledGetter: () => boolean,
        capture?: boolean
    ): () => void;
    bindJumpToToken(
        onJumpToToken: (event: ShortcutEvent, target: TokenJumpTarget, forward: boolean) => boolean,
        disabledGetter: () => boolean,
        capture?: boolean
    ): () => void;
}

export class DefaultKeyBinder implements KeyBinder {
    private readonly keyBindSet: KeyBindSet;

    constructor(keyBindSet: KeyBindSet) {
        this.keyBindSet = keyBindSet;
    }
    bindCopy<T extends SubtitleModel = SubtitleModel>(
        onCopy: (event: ShortcutEvent, subtitle: T) => void,
        disabledGetter: () => boolean,
        subtitleGetter: () => T | undefined,
        capture = false
    ) {
        const shortcut = this.keyBindSet.copySubtitle.keys;

        if (!shortcut) {
            return () => {};
        }

        const handler = this.copyHandler(onCopy, disabledGetter, subtitleGetter);
        return this._bind(shortcut, capture, handler);
    }

    copyHandler<T extends SubtitleModel>(
        onCopy: (event: ShortcutEvent, subtitle: T) => void,
        disabledGetter: () => boolean,
        subtitleGetter: () => T | undefined
    ) {
        return (event: ShortcutEvent) => {
            if (disabledGetter()) {
                return false;
            }

            const subtitle = subtitleGetter();

            if (!subtitle) {
                return false;
            }

            onCopy(event, subtitle);
            return true;
        };
    }

    bindAnkiExport(onAnkiExport: (event: ShortcutEvent) => void, disabledGetter: () => boolean, capture = false) {
        const shortcut = this.keyBindSet.ankiExport.keys;

        if (!shortcut) {
            return () => {};
        }

        const handler = this.ankiExportHandler(onAnkiExport, disabledGetter);
        return this._bind(shortcut, capture, handler);
    }

    ankiExportHandler(onAnkiExport: (event: ShortcutEvent) => void, disabledGetter: () => boolean) {
        return (event: ShortcutEvent) => {
            if (disabledGetter()) {
                return false;
            }

            onAnkiExport(event);
            return true;
        };
    }

    bindUpdateLastCard(
        onUpdateLastCard: (event: ShortcutEvent) => void,
        disabledGetter: () => boolean,
        capture = false
    ) {
        const shortcut = this.keyBindSet.updateLastCard.keys;

        if (!shortcut) {
            return () => {};
        }

        const handler = this.updateLastCardHandler(onUpdateLastCard, disabledGetter);
        return this._bind(shortcut, capture, handler);
    }

    updateLastCardHandler(onUpdateLastCard: (event: ShortcutEvent) => void, disabledGetter: () => boolean) {
        return (event: ShortcutEvent) => {
            if (disabledGetter()) {
                return false;
            }

            onUpdateLastCard(event);
            return true;
        };
    }

    bindTakeScreenshot(
        onTakeScreenshot: (event: ShortcutEvent) => void,
        disabledGetter: () => boolean,
        capture = false
    ) {
        const shortcut = this.keyBindSet.takeScreenshot.keys;

        if (!shortcut) {
            return () => {};
        }

        const handler = this.takeScreenshotHandler(onTakeScreenshot, disabledGetter);
        return this._bind(shortcut, capture, handler);
    }

    takeScreenshotHandler(onTakeScreenshot: (event: ShortcutEvent) => void, disabledGetter: () => boolean) {
        return (event: ShortcutEvent) => {
            if (disabledGetter()) {
                return false;
            }

            onTakeScreenshot(event);
            return true;
        };
    }

    bindToggleRecording(
        onToggleRecording: (event: ShortcutEvent) => void,
        disabledGetter: () => boolean,
        capture = false
    ) {
        const shortcut = this.keyBindSet.toggleRecording.keys;

        if (!shortcut) {
            return () => {};
        }

        const handler = this.takeScreenshotHandler(onToggleRecording, disabledGetter);
        return this._bind(shortcut, capture, handler);
    }

    toggleRecordingHandler(onToggleRecording: (event: ShortcutEvent) => void, disabledGetter: () => boolean) {
        return (event: ShortcutEvent) => {
            if (disabledGetter()) {
                return false;
            }

            onToggleRecording(event);
            return true;
        };
    }

    bindSelectSubtitleTrack(
        onSelectSubtitleTrack: (event: ShortcutEvent) => void,
        disabledGetter: () => boolean,
        capture = false
    ) {
        const shortcut = this.keyBindSet.selectSubtitleTrack.keys;

        if (!shortcut) {
            return () => {};
        }

        const handler = this.selectSubtitleTrackHandler(onSelectSubtitleTrack, disabledGetter);
        return this._bind(shortcut, capture, handler);
    }

    selectSubtitleTrackHandler(onSelectSubtitleTrack: (event: ShortcutEvent) => void, disabledGetter: () => boolean) {
        return (event: ShortcutEvent) => {
            if (disabledGetter()) {
                return false;
            }

            onSelectSubtitleTrack(event);
            return true;
        };
    }

    bindSeekToSubtitle(
        onSeekToSubtitle: (event: ShortcutEvent, subtitle: SubtitleModel) => void,
        disabledGetter: () => boolean,
        timeGetter: () => number,
        subtitlesGetter: () => SubtitleModel[] | undefined,
        seekableTracksGetter: () => SeekableTracks,
        capture = false
    ) {
        const delegate = (event: ShortcutEvent, forward: boolean) => {
            if (disabledGetter()) {
                return false;
            }

            const subtitles = subtitlesGetter();

            if (!subtitles || subtitles.length === 0) {
                return false;
            }

            const subtitle = adjacentSubtitle(forward, timeGetter(), subtitles, seekableTracksGetter());

            if (subtitle !== null && subtitle.start >= 0 && subtitle.end >= 0) {
                onSeekToSubtitle(event, subtitle);
                return true;
            }

            return false;
        };
        const previousShortcut = this.keyBindSet.seekToPreviousSubtitle.keys;
        const nextShortcut = this.keyBindSet.seekToNextSubtitle.keys;
        const previousHandler = (event: ShortcutEvent) => delegate(event, false);
        const nextHandler = (event: ShortcutEvent) => delegate(event, true);

        let unbindPrevious: (() => void) | undefined;
        let unbindNext: (() => void) | undefined;

        if (previousShortcut) {
            unbindPrevious = this._bind(previousShortcut, capture, previousHandler);
        }

        if (nextShortcut) {
            unbindNext = this._bind(nextShortcut, capture, nextHandler);
        }

        return () => {
            unbindPrevious?.();
            unbindNext?.();
        };
    }

    bindSeekToBeginningOfCurrentSubtitle(
        onSeekToBeginningOfCurrentSubtitle: (event: ShortcutEvent, subtitle: SubtitleModel) => void,
        disabledGetter: () => boolean,
        timeGetter: () => number,
        subtitlesGetter: () => SubtitleModel[] | undefined,
        seekableTracksGetter: () => SeekableTracks,
        capture = false
    ) {
        const shortcut = this.keyBindSet.seekToBeginningOfCurrentSubtitle.keys;

        if (!shortcut) {
            return () => {};
        }

        const handler = (event: ShortcutEvent) => {
            if (disabledGetter()) {
                return false;
            }

            const subtitles = subtitlesGetter();

            if (!subtitles || subtitles.length === 0) {
                return false;
            }

            const subtitle = this._currentOrPreviousSubtitle(timeGetter(), subtitles, seekableTracksGetter());

            if (subtitle !== undefined && subtitle.start >= 0 && subtitle.end >= 0) {
                onSeekToBeginningOfCurrentSubtitle(event, subtitle);
                return true;
            }

            return false;
        };
        return this._bind(shortcut, capture, handler);
    }

    _currentOrPreviousSubtitle(time: number, subtitles: SubtitleModel[], seekableTracks: SeekableTracks) {
        const now = time;
        let currentSubtitle: SubtitleModel | undefined;
        let previousSubtitle: SubtitleModel | undefined;
        let minDiff = Number.MAX_SAFE_INTEGER;

        for (let i = 0; i < subtitles.length; ++i) {
            const s = subtitles[i];

            if (!isTrackSeekable(seekableTracks, s.track) || s.start < 0 || s.end < 0) {
                continue;
            }

            const diff = now - s.start;

            if (now >= s.start && now < s.end) {
                if (diff < minDiff) {
                    currentSubtitle = s;
                    minDiff = diff;
                }
            } else if (now > s.end) {
                previousSubtitle = s;
            }
        }

        return currentSubtitle ? currentSubtitle : previousSubtitle;
    }

    bindSeekBackwardOrForward(
        onSeekBackwardOrForward: (event: ShortcutEvent, forward: boolean) => void,
        disabledGetter: () => boolean,
        capture = false
    ) {
        const delegate = (event: ShortcutEvent, forward: boolean) => {
            if (disabledGetter()) {
                return false;
            }

            onSeekBackwardOrForward(event, forward);
            return true;
        };
        const backShortcut = this.keyBindSet.seekBackward.keys;
        const nextShortcut = this.keyBindSet.seekForward.keys;
        const backHandler = (event: ShortcutEvent) => delegate(event, false);
        const nextHandler = (event: ShortcutEvent) => delegate(event, true);

        let unbindBack: (() => void) | undefined;
        let unbindNext: (() => void) | undefined;

        if (backShortcut) {
            unbindBack = this._bind(backShortcut, capture, backHandler);
        }

        if (nextShortcut) {
            unbindNext = this._bind(nextShortcut, capture, nextHandler);
        }

        return () => {
            unbindBack?.();
            unbindNext?.();
        };
    }

    bindOffsetToSubtitle(
        onOffsetChange: (event: ShortcutEvent, newOffset: number) => void,
        disabledGetter: () => boolean,
        timeGetter: () => number,
        subtitlesGetter: () => SubtitleModel[] | undefined,
        seekableTracksGetter: () => SeekableTracks,
        capture = false
    ) {
        const delegate = (event: ShortcutEvent, forward: boolean) => {
            if (disabledGetter()) {
                return false;
            }

            const subtitles = subtitlesGetter();

            if (!subtitles || subtitles.length === 0) {
                return false;
            }

            const time = timeGetter();
            const subtitle = adjacentSubtitle(forward, time, subtitles, seekableTracksGetter());

            if (subtitle !== null) {
                const subtitleStart = subtitle.originalStart;
                const newOffset = time - subtitleStart;
                onOffsetChange(event, newOffset);
                return true;
            }

            return false;
        };
        const previousShortcut = this.keyBindSet.adjustOffsetToPreviousSubtitle.keys;
        const nextShortcut = this.keyBindSet.adjustOffsetToNextSubtitle.keys;
        const previousHandler = (event: ShortcutEvent) => delegate(event, false);
        const nextHandler = (event: ShortcutEvent) => delegate(event, true);

        let unbindPrevious: (() => void) | undefined;
        let unbindNext: (() => void) | undefined;

        if (previousShortcut) {
            unbindPrevious = this._bind(previousShortcut, capture, previousHandler);
        }

        if (nextShortcut) {
            unbindNext = this._bind(nextShortcut, capture, nextHandler);
        }

        return () => {
            unbindPrevious?.();
            unbindNext?.();
        };
    }

    bindAdjustOffset(
        onOffsetChange: (event: ShortcutEvent, newOffset: number) => void,
        disabledGetter: () => boolean,
        subtitlesGetter: () => SubtitleModel[] | undefined,
        capture = false
    ) {
        const delegate = (event: ShortcutEvent, increase: boolean) => {
            if (disabledGetter()) {
                return false;
            }

            const subtitles = subtitlesGetter();

            if (!subtitles || subtitles.length === 0) {
                return false;
            }

            const currentOffset = subtitles[0].start - subtitles[0].originalStart;
            const newOffset = currentOffset + (increase ? 100 : -100);
            onOffsetChange(event, newOffset);
            return true;
        };

        const decreaseShortcut = this.keyBindSet.decreaseOffset.keys;
        const increaseShortcut = this.keyBindSet.increaseOffset.keys;
        const decreaseHandler = (event: ShortcutEvent) => delegate(event, false);
        const increaseHandler = (event: ShortcutEvent) => delegate(event, true);

        const unbindDecrease = decreaseShortcut ? this._bind(decreaseShortcut, capture, decreaseHandler) : () => {};
        const unbindIncrease = increaseShortcut ? this._bind(increaseShortcut, capture, increaseHandler) : () => {};
        return () => {
            unbindDecrease();
            unbindIncrease();
        };
    }

    bindResetOffet(
        onResetOffset: (event: ShortcutEvent) => void,
        disabledGetter: () => boolean,
        capture?: boolean | undefined
    ) {
        const shortcut = this.keyBindSet.resetOffset.keys;

        if (!shortcut) {
            return () => {};
        }

        const handler = (event: ShortcutEvent) => {
            if (disabledGetter()) {
                return false;
            }

            onResetOffset(event);
            return true;
        };

        return this._bind(shortcut, capture ?? false, handler);
    }

    bindAdjustPlaybackRate(
        onAdjustPlaybackRate: (event: ShortcutEvent, increase: boolean) => void,
        disabledGetter: () => boolean,
        capture = false
    ) {
        const delegate = (event: ShortcutEvent, increase: boolean) => {
            if (disabledGetter()) {
                return false;
            }

            onAdjustPlaybackRate(event, increase);
            return true;
        };
        const increaseShortcut = this.keyBindSet.increasePlaybackRate.keys;
        const decreaseShortcut = this.keyBindSet.decreasePlaybackRate.keys;
        const decreaseHandler = (event: ShortcutEvent) => delegate(event, false);
        const increaseHandler = (event: ShortcutEvent) => delegate(event, true);
        let unbindDecrease: (() => void) | undefined;
        let unbindIncrease: (() => void) | undefined;

        if (decreaseShortcut) {
            unbindDecrease = this._bind(decreaseShortcut, capture, decreaseHandler);
        }

        if (increaseShortcut) {
            unbindIncrease = this._bind(increaseShortcut, capture, increaseHandler);
        }

        return () => {
            unbindDecrease?.();
            unbindIncrease?.();
        };
    }

    bindAdjustSubtitlePositionOffset(
        onAdjustSubtitlePositionOffset: (event: ShortcutEvent, increase: boolean) => void,
        disabledGetter: () => boolean,
        capture = false
    ) {
        const delegate = (event: ShortcutEvent, increase: boolean) => {
            if (disabledGetter()) {
                return false;
            }

            onAdjustSubtitlePositionOffset(event, increase);
            return true;
        };

        const increaseShortcut = this.keyBindSet.moveBottomSubtitlesUp.keys;
        const decreaseShortcut = this.keyBindSet.moveBottomSubtitlesDown.keys;

        const decreaseHandler = (event: ShortcutEvent) => delegate(event, false);
        const increaseHandler = (event: ShortcutEvent) => delegate(event, true);

        const unbindDecrease = decreaseShortcut ? this._bind(decreaseShortcut, capture, decreaseHandler) : () => {};
        const unbindIncrease = increaseShortcut ? this._bind(increaseShortcut, capture, increaseHandler) : () => {};

        return () => {
            unbindDecrease();
            unbindIncrease();
        };
    }

    bindAdjustTopSubtitlePositionOffset(
        onAdjustTopSubtitlePositionOffset: (event: ShortcutEvent, increase: boolean) => void,
        disabledGetter: () => boolean,
        capture = false
    ) {
        const delegate = (event: ShortcutEvent, increase: boolean) => {
            if (disabledGetter()) {
                return false;
            }

            onAdjustTopSubtitlePositionOffset(event, increase);
            return true;
        };

        const increaseShortcut = this.keyBindSet.moveTopSubtitlesDown.keys;
        const decreaseShortcut = this.keyBindSet.moveTopSubtitlesUp.keys;

        const decreaseHandler = (event: ShortcutEvent) => delegate(event, false);
        const increaseHandler = (event: ShortcutEvent) => delegate(event, true);

        const unbindDecrease = decreaseShortcut ? this._bind(decreaseShortcut, capture, decreaseHandler) : () => {};
        const unbindIncrease = increaseShortcut ? this._bind(increaseShortcut, capture, increaseHandler) : () => {};

        return () => {
            unbindDecrease();
            unbindIncrease();
        };
    }

    bindToggleSubtitles(
        onToggleSubtitles: (event: ShortcutEvent) => void,
        disabledGetter: () => boolean,
        capture = false
    ) {
        const shortcut = this.keyBindSet.toggleSubtitles.keys;

        if (!shortcut) {
            return () => {};
        }

        const handler = (event: ShortcutEvent) => {
            if (disabledGetter()) {
                return false;
            }

            onToggleSubtitles(event);
            return true;
        };
        return this._bind(shortcut, capture, handler);
    }

    bindToggleSubtitleStudy(
        onToggleSubtitleStudy: (event: ShortcutEvent, mode: 'blur' | 'hide') => void,
        disabledGetter: () => boolean,
        capture = false
    ) {
        const unbindHandlers = (['blur', 'hide'] as const).map((mode) => {
            const shortcut =
                this.keyBindSet[mode === 'blur' ? 'toggleSubtitleStudyBlur' : 'toggleSubtitleStudyHide'].keys;
            if (!shortcut) return () => {};
            return this._bind(shortcut, capture, (event) => {
                if (disabledGetter()) return false;
                onToggleSubtitleStudy(event, mode);
                return true;
            });
        });
        return () => unbindHandlers.forEach((unbind) => unbind());
    }

    bindToggleSubtitleTrackInVideo(
        onToggleSubtitleTrack: (event: ShortcutEvent, extra: any) => void,
        disabledGetter: () => boolean,
        capture = false
    ) {
        const shortcuts = [
            this.keyBindSet.toggleVideoSubtitleTrack1.keys,
            this.keyBindSet.toggleVideoSubtitleTrack2.keys,
            this.keyBindSet.toggleVideoSubtitleTrack3.keys,
        ].filter((s) => s);

        if (shortcuts.length === 0) {
            return () => {};
        }

        const delegate = (event: ShortcutEvent, track: number) => {
            if (disabledGetter()) {
                return false;
            }

            onToggleSubtitleTrack(event, track);
            return true;
        };
        const unbindHandlers: (() => void)[] = [];

        for (let i = 0; i < shortcuts.length; ++i) {
            const handler = (event: ShortcutEvent) => delegate(event, i);
            const unbindHandler = shortcuts[i] ? this._bind(shortcuts[i], capture, handler) : () => {};
            unbindHandlers.push(unbindHandler);
        }

        return () => {
            for (let i = 0; i < shortcuts.length; ++i) {
                const unbindHandler = unbindHandlers[i];
                unbindHandler();
            }
        };
    }

    bindToggleSubtitleTrackInList(
        onToggleSubtitleTrackInList: (event: ShortcutEvent, extra: any) => void,
        disabledGetter: () => boolean,
        capture = false
    ) {
        const shortcuts = [
            this.keyBindSet.toggleAsbplayerSubtitleTrack1.keys,
            this.keyBindSet.toggleAsbplayerSubtitleTrack2.keys,
            this.keyBindSet.toggleAsbplayerSubtitleTrack3.keys,
        ].filter((s) => s);

        if (shortcuts.length === 0) {
            return () => {};
        }

        const delegate = (event: ShortcutEvent, track: number) => {
            if (disabledGetter()) {
                return false;
            }

            onToggleSubtitleTrackInList(event, track);
            return true;
        };

        const unbindHandlers: (() => void)[] = [];

        for (let i = 0; i < 9; ++i) {
            const handler = (event: ShortcutEvent) => delegate(event, i);
            const unbindHandler = shortcuts[i] ? this._bind(shortcuts[i], capture, handler) : () => {};
            unbindHandlers.push(unbindHandler);
        }

        return () => {
            for (let i = 0; i < 9; ++i) {
                const unbindHandler = unbindHandlers[i];
                unbindHandler();
            }
        };
    }

    bindUnblurTrack(
        onUnblurTrack: (event: ShortcutEvent, track: number) => void,
        disabledGetter: () => boolean,
        capture = false
    ) {
        const shortcuts = [
            this.keyBindSet.unblurAsbplayerTrack1.keys,
            this.keyBindSet.unblurAsbplayerTrack2.keys,
            this.keyBindSet.unblurAsbplayerTrack3.keys,
        ].filter((s) => s);

        if (shortcuts.length === 0) {
            return () => {};
        }

        const delegate = (event: ShortcutEvent, track: number) => {
            if (disabledGetter()) {
                return false;
            }

            onUnblurTrack(event, track);
            return true;
        };
        const unbindHandlers: (() => void)[] = [];

        for (let i = 0; i < shortcuts.length; ++i) {
            const handler = (event: ShortcutEvent) => delegate(event, i);
            const unbindHandler = shortcuts[i] ? this._bind(shortcuts[i], capture, handler) : () => {};
            unbindHandlers.push(unbindHandler);
        }

        return () => {
            for (let i = 0; i < shortcuts.length; ++i) {
                const unbindHandler = unbindHandlers[i];
                unbindHandler();
            }
        };
    }

    bindPlay(onPlay: (event: ShortcutEvent) => void, disabledGetter: () => boolean, capture = false) {
        const shortcut = this.keyBindSet.togglePlay.keys;

        if (!shortcut) {
            return () => {};
        }

        const handler = (event: ShortcutEvent) => {
            if (disabledGetter()) {
                return false;
            }

            onPlay(event);
            return true;
        };

        return this._bind(shortcut, capture, handler);
    }

    bindAutoPause(onAutoPause: (event: ShortcutEvent) => void, disabledGetter: () => boolean, capture = false) {
        const shortcut = this.keyBindSet.toggleAutoPause.keys;

        if (!shortcut) {
            return () => {};
        }

        const handler = (event: ShortcutEvent) => {
            if (disabledGetter()) {
                return false;
            }

            onAutoPause(event);
            return true;
        };

        return this._bind(shortcut, capture, handler);
    }

    bindCondensedPlayback(
        onCondensedPlayback: (event: ShortcutEvent) => void,
        disabledGetter: () => boolean,
        capture = false
    ) {
        const shortcut = this.keyBindSet.toggleCondensedPlayback.keys;

        if (!shortcut) {
            return () => {};
        }

        const handler = (event: ShortcutEvent) => {
            if (disabledGetter()) {
                return false;
            }

            onCondensedPlayback(event);
            return true;
        };

        return this._bind(shortcut, capture, handler);
    }

    bindFastForwardPlayback(
        onFastForwardPlayback: (event: ShortcutEvent) => void,
        disabledGetter: () => boolean,
        capture = false
    ) {
        const shortcut = this.keyBindSet.toggleFastForwardPlayback.keys;

        if (!shortcut) {
            return () => {};
        }

        const handler = (event: ShortcutEvent) => {
            if (disabledGetter()) {
                return false;
            }

            onFastForwardPlayback(event);
            return true;
        };

        return this._bind(shortcut, capture, handler);
    }

    bindToggleSidePanel(
        onToggleSidePanel: (event: ShortcutEvent) => void,
        disabledGetter: () => boolean,
        capture = false
    ) {
        const shortcut = this.keyBindSet.toggleSidePanel.keys;

        if (!shortcut) {
            return () => {};
        }

        const handler = (event: ShortcutEvent) => {
            if (disabledGetter()) {
                return false;
            }

            onToggleSidePanel(event);
            return true;
        };

        return this._bind(shortcut, capture, handler);
    }

    bindToggleRepeat(onToggleRepeat: (event: ShortcutEvent) => void, disabledGetter: () => boolean, capture = false) {
        const shortcut = this.keyBindSet.toggleRepeat.keys;

        const handler = (event: ShortcutEvent) => {
            if (disabledGetter()) {
                return false;
            }

            onToggleRepeat(event);
            return true;
        };

        return this._bind(shortcut, capture, handler);
    }

    bindCycleAutoPauseResumeMode(
        onCycleAutoPauseResumeMode: (event: ShortcutEvent) => void,
        disabledGetter: () => boolean,
        capture = false
    ) {
        const shortcut = this.keyBindSet.cycleAutoPauseResumeMode.keys;

        const handler = (event: ShortcutEvent) => {
            if (disabledGetter()) {
                return false;
            }

            onCycleAutoPauseResumeMode(event);
            return true;
        };

        return this._bind(shortcut, capture, handler);
    }

    bindToggleSubtitleVisibility(
        onToggleSubtitleVisibility: (event: ShortcutEvent) => void,
        disabledGetter: () => boolean,
        capture = false
    ) {
        const shortcut = this.keyBindSet.toggleSubtitleVisibility.keys;

        const handler = (event: ShortcutEvent) => {
            if (disabledGetter()) {
                return false;
            }

            onToggleSubtitleVisibility(event);
            return true;
        };

        return this._bind(shortcut, capture, handler);
    }

    bindOpenStatistics(
        onOpenStatistics: (event: ShortcutEvent) => void,
        disabledGetter: () => boolean,
        capture = false
    ) {
        const shortcut = this.keyBindSet.openStatistics.keys;

        if (!shortcut) {
            return () => {};
        }

        const handler = (event: ShortcutEvent) => {
            if (disabledGetter()) {
                return false;
            }

            onOpenStatistics(event);
            return true;
        };
        return this._bind(shortcut, capture, handler);
    }

    bindMarkHoveredToken(
        onMarkHoveredToken: (event: ShortcutEvent, tokenStatus: TokenStatus) => void,
        disabledGetter: () => boolean,
        capture = false
    ) {
        const shortcuts = [
            this.keyBindSet.markHoveredToken0.keys,
            this.keyBindSet.markHoveredToken1.keys,
            this.keyBindSet.markHoveredToken2.keys,
            this.keyBindSet.markHoveredToken3.keys,
            this.keyBindSet.markHoveredToken4.keys,
            this.keyBindSet.markHoveredToken5.keys,
        ];

        if (shortcuts.length === 0) {
            return () => {};
        }

        const delegate = (event: ShortcutEvent, tokenStatus: TokenStatus) => {
            if (disabledGetter()) {
                return false;
            }

            onMarkHoveredToken(event, tokenStatus);
            return true;
        };
        const unbindHandlers: (() => void)[] = [];

        for (let i = 0; i < shortcuts.length; ++i) {
            const handler = (event: ShortcutEvent) => delegate(event, i);
            const unbindHandler = shortcuts[i] ? this._bind(shortcuts[i], capture, handler) : () => {};
            unbindHandlers.push(unbindHandler);
        }

        return () => {
            for (let i = 0; i < shortcuts.length; ++i) {
                const unbindHandler = unbindHandlers[i];
                unbindHandler();
            }
        };
    }

    bindToggleHoveredTokenIgnored(
        onToggleHoveredTokenIgnored: (event: ShortcutEvent) => void,
        disabledGetter: () => boolean,
        capture = false
    ) {
        const shortcut = this.keyBindSet.toggleHoveredTokenIgnored.keys;

        if (!shortcut) {
            return () => {};
        }

        const handler = (event: ShortcutEvent) => {
            if (disabledGetter()) {
                return false;
            }

            onToggleHoveredTokenIgnored(event);
            return true;
        };
        return this._bind(shortcut, capture, handler);
    }

    bindJumpToToken(
        onJumpToToken: (event: ShortcutEvent, target: TokenJumpTarget, forward: boolean) => boolean,
        disabledGetter: () => boolean,
        capture = false
    ) {
        const tokenKeyBinds: [TokenJumpTarget, string, string][] = [
            [{ kind: 'any' }, this.keyBindSet.jumpToPreviousToken.keys, this.keyBindSet.jumpToNextToken.keys],
            [
                { kind: 'status', value: TokenStatus.UNCOLLECTED },
                this.keyBindSet.jumpToPreviousTokenStatus0.keys,
                this.keyBindSet.jumpToNextTokenStatus0.keys,
            ],
            [
                { kind: 'status', value: TokenStatus.UNKNOWN },
                this.keyBindSet.jumpToPreviousTokenStatus1.keys,
                this.keyBindSet.jumpToNextTokenStatus1.keys,
            ],
            [
                { kind: 'status', value: TokenStatus.LEARNING },
                this.keyBindSet.jumpToPreviousTokenStatus2.keys,
                this.keyBindSet.jumpToNextTokenStatus2.keys,
            ],
            [
                { kind: 'status', value: TokenStatus.GRADUATED },
                this.keyBindSet.jumpToPreviousTokenStatus3.keys,
                this.keyBindSet.jumpToNextTokenStatus3.keys,
            ],
            [
                { kind: 'status', value: TokenStatus.YOUNG },
                this.keyBindSet.jumpToPreviousTokenStatus4.keys,
                this.keyBindSet.jumpToNextTokenStatus4.keys,
            ],
            [
                { kind: 'status', value: TokenStatus.MATURE },
                this.keyBindSet.jumpToPreviousTokenStatus5.keys,
                this.keyBindSet.jumpToNextTokenStatus5.keys,
            ],
            [
                { kind: 'state', value: TokenState.IGNORED },
                this.keyBindSet.jumpToPreviousTokenState0.keys,
                this.keyBindSet.jumpToNextTokenState0.keys,
            ],
        ];
        const bindings: { shortcut: string; target: TokenJumpTarget; forward: boolean }[] = [];

        for (const [target, previousShortcut, nextShortcut] of tokenKeyBinds) {
            if (previousShortcut) {
                bindings.push({ shortcut: previousShortcut, target, forward: false });
            }
            if (nextShortcut) {
                bindings.push({ shortcut: nextShortcut, target, forward: true });
            }
        }
        if (!bindings.length) return () => {};

        const delegate = (event: ShortcutEvent, target: TokenJumpTarget, forward: boolean) => {
            if (disabledGetter()) return false;

            return onJumpToToken(event, target, forward);
        };
        const unbindHandlers = bindings.map(({ shortcut, target, forward }) =>
            this._bind(shortcut, capture, (event) => delegate(event, target, forward))
        );

        return () => {
            for (const unbindHandler of unbindHandlers) unbindHandler();
        };
    }

    private _bind(shortcut: string, capture: boolean, handler: (event: ShortcutEvent) => boolean) {
        if (isMouseShortcut(shortcut)) return bindMouseShortcut(shortcut, capture, handler);
        let handled: boolean | undefined = undefined;

        const wrappedHandler = (event: ShortcutEvent) => {
            if (event.type === 'keydown') {
                handled = handler(event);
            } else if (event.type === 'keyup') {
                if (handled) {
                    event.preventDefault();
                    event.stopImmediatePropagation();
                }

                handled = undefined;
            }
        };

        hotkeys(shortcut, { capture, keydown: true, keyup: true }, wrappedHandler);
        return () => hotkeys.unbind(shortcut, wrappedHandler);
    }
}
